import { GetUserOnboardingStatusResponse } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/plg/v1/plg_service.pb';
import { OnboardingTaskProgress } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/message.pb';
import { OnboardingBaseTask, TaskStatus } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { QueryKey, useQueryClient } from 'react-query';
import { serviceName } from '../service';
import { UpdateOnboardingStatusIO } from '../types';
import {
  UseSchemaCallIntelligencePLGV1MutationEndpointArgs,
  useSchemaCallIntelligencePLGV1Mutation,
} from './use-call-intelligence-v1-mutation';

type PreviousOnboardingStatusQueries = Array<[QueryKey, GetUserOnboardingStatusResponse | undefined]>;

type MutationContext<C = unknown> = {
  previousOnboardingStatusQueries?: PreviousOnboardingStatusQueries;
  otherContext?: C;
};

type BaseOnboardingTaskProgress = OnboardingTaskProgress & {
  baseTask: OnboardingBaseTask;
};

function isBaseOnboardingTaskProgress(task: OnboardingTaskProgress): task is BaseOnboardingTaskProgress {
  return 'baseTask' in task;
}

function getBaseTaskFromRequest(task: UpdateOnboardingStatusIO['input']['task'] | undefined) {
  return task && 'baseTask' in task ? task.baseTask : undefined;
}

const GET_USER_ONBOARDING_STATUS_QUERY_KEY = [serviceName, 'GetUserOnboardingStatus'] as const;

/**
 * A hook that returns a mutation for the `UpdateOnboardingStatus` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useUpdateOnboardingStatusMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends UpdateOnboardingStatusIO['input'] = UpdateOnboardingStatusIO['input']
>({
  options,
  ...args
}: UseSchemaCallIntelligencePLGV1MutationEndpointArgs<
  'UpdateOnboardingStatus',
  E,
  C | undefined,
  OtherOptions,
  RequestOverride
> = {}) => {
  const queryClient = useQueryClient();

  return useSchemaCallIntelligencePLGV1Mutation<
    'UpdateOnboardingStatus',
    E,
    MutationContext<C>,
    OtherOptions,
    RequestOverride
  >({
    endpointName: 'UpdateOnboardingStatus',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        await queryClient.cancelQueries(GET_USER_ONBOARDING_STATUS_QUERY_KEY);

        const previousOnboardingStatusQueries = queryClient.getQueriesData<GetUserOnboardingStatusResponse | undefined>(
          GET_USER_ONBOARDING_STATUS_QUERY_KEY
        );

        const baseTask = getBaseTaskFromRequest(request.task);
        const taskStatus = request.task?.status ?? TaskStatus.TASK_STATUS_COMPLETED;

        if (baseTask && request.status) {
          queryClient.setQueriesData<GetUserOnboardingStatusResponse | undefined>(
            GET_USER_ONBOARDING_STATUS_QUERY_KEY,
            (prev) => {
              if (!prev) return prev;

              const existingTasks = prev.tasks ?? [];

              const hasTask = existingTasks.some(
                (task) => isBaseOnboardingTaskProgress(task) && task.baseTask === baseTask
              );

              const nextTasks = hasTask
                ? existingTasks.map((task) =>
                    isBaseOnboardingTaskProgress(task) && task.baseTask === baseTask
                      ? {
                          ...task,
                          status: taskStatus,
                        }
                      : task
                  )
                : [
                    ...existingTasks,
                    {
                      baseTask,
                      status: taskStatus,
                    },
                  ];

              return {
                ...prev,
                status: request.status,
                tasks: nextTasks,
              };
            }
          );
        }

        return {
          previousOnboardingStatusQueries,
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        // Implement query updates or invalidation for affected queries here

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        context?.previousOnboardingStatusQueries?.forEach(([queryKey, previousData]) => {
          queryClient.setQueryData(queryKey, previousData);
        });

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        queryClient.invalidateQueries(GET_USER_ONBOARDING_STATUS_QUERY_KEY);

        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
